import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Mail, Clock, CheckCircle2, XCircle, AlertTriangle,
  TrendingUp, Brain, Trophy, Calendar, Send, ListChecks,
} from 'lucide-react';
import { logger } from '../lib/logger';

const KIND_LABEL = {
  day2_check_in: 'Day 2 · Check-in',
  day5_product_story: 'Day 5 · Product story',
  day7_trial_offer: 'Day 7 · Trial offer',
};
const STATUS_COLOR = {
  sent:      { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
  queued:    { bg: 'bg-[#004C54]/8', text: 'text-[#004C54]',   border: 'border-[#004C54]/20', icon: Clock },
  cancelled: { bg: 'bg-amber-50',    text: 'text-amber-700',  border: 'border-amber-200',   icon: XCircle },
  failed:    { bg: 'bg-rose-50',     text: 'text-rose-700',   border: 'border-rose-200',    icon: AlertTriangle },
};
const STATUS_ORDER = ['queued', 'sent', 'cancelled', 'failed'];
const KIND_ORDER = ['day2_check_in', 'day5_product_story', 'day7_trial_offer'];
const ARCHETYPE_LABEL = {
  grinder: 'The Grinder', hopeful: 'The Hopeful',
  phoenix: 'The Phoenix', natural: 'The Natural',
};

// ---------- Small subcomponents --------------------------------------------

const StatTile = ({ icon: Icon, label, value, sub, accent = '#004C54', testid }) => (
  <Card className="border-border" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-foreground/60 font-semibold">
          {label}
        </div>
      </div>
      <div className="font-heading text-3xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-foreground/60 mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

const Bar = ({ label, count, max, accent = '#004C54', testid }) => {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-foreground/60 tabular-nums">{count}</span>
      </div>
      <div className="h-2 rounded-full bg-foreground/8 overflow-hidden">
        <div className="h-full rounded-full transition-[width]"
             style={{ width: `${pct}%`, backgroundColor: accent }} />
      </div>
    </div>
  );
};

const Timeline = ({ days }) => {
  // Always render the last 14 calendar days (so empty days show as zero bars)
  const today = new Date();
  const slots = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const match = (days || []).find((x) => x.day === key);
    slots.push({ day: key, sent: match ? match.sent : 0,
                 label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) });
  }
  const max = Math.max(1, ...slots.map((s) => s.sent));
  return (
    <div className="flex items-end gap-1.5 h-32" data-testid="drip-timeline">
      {slots.map((s) => {
        const h = Math.max(4, Math.round((s.sent / max) * 100));
        return (
          <div key={s.day} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full bg-foreground/10 rounded-t-sm rounded-b-sm overflow-hidden relative" style={{ height: '100%' }}>
              <div
                className="absolute bottom-0 left-0 right-0 bg-[#004C54] transition-[height]"
                style={{ height: `${h}%` }}
                title={`${s.label}: ${s.sent} sent`}
              />
            </div>
            <div className="text-[9px] font-mono-accent text-foreground/55 leading-none">
              {s.label.split(' ')[1]}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_COLOR[status] || STATUS_COLOR.queued;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border} text-[11px] font-semibold uppercase tracking-wider`}>
      <Icon className="h-3 w-3" />{status}
    </span>
  );
};

// ---------- Main page ------------------------------------------------------

export default function AdminDripStats() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    Promise.all([adminApi.dripStats(), adminApi.recentDrips(30)])
      .then(([s, r]) => { setStats(s); setRecent(r.items || []); })
      .catch((e) => { logger.error('drip stats failed', e); setError(e.message || 'Could not load drip stats.'); });
  };
  useEffect(() => {
    load();
    // poll every 60s so the dashboard stays fresh while open
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const archetypeMax = useMemo(() => {
    if (!stats?.by_archetype) return 0;
    return Math.max(1, ...Object.values(stats.by_archetype));
  }, [stats]);
  const sportMax = useMemo(() => {
    if (!stats?.by_sport) return 0;
    return Math.max(1, ...Object.values(stats.by_sport));
  }, [stats]);

  // Memoize the sorted entry lists so we don't re-run Object.entries + sort
  // on every render (these only change when `stats` is refetched).
  const archetypeEntries = useMemo(
    () => Object.entries(stats?.by_archetype || {}).sort((a, b) => b[1] - a[1]),
    [stats],
  );
  const sportEntries = useMemo(
    () => Object.entries(stats?.by_sport || {}).sort((a, b) => b[1] - a[1]),
    [stats],
  );

  if (error) {
    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
      </Card>
    );
  }
  if (!stats) {
    return (
      <div className="space-y-3" data-testid="admin-drips-loading">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  const t = stats.totals || {};
  const funnel = stats.lead_funnel || {};
  return (
    <div className="space-y-6" data-testid="admin-drip-stats-page">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-[#004C54]" />
        <h2 className="font-heading text-lg font-bold">Welcome Drip Analytics</h2>
        <Badge variant="outline" className="border-foreground/15 text-foreground/60">
          {t.total || 0} total drip rows
        </Badge>
      </div>
      <p className="text-sm text-foreground/60">
        Live view of the 4-touch welcome sequence (Day 0 → Day 2 → Day 5 → Day 7) and the
        quiz funnel that feeds it. Updates every 60 seconds.
      </p>

      {/* ----- TOP-LINE STAT TILES ----- */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile icon={Send} label="Sent" value={t.sent || 0}
                  sub={`${stats.recent_24h?.sent || 0} in the last 24h`}
                  testid="stat-sent" />
        <StatTile icon={Clock} label="Queued" value={t.queued || 0}
                  sub="Waiting for next scheduled fire" accent="#004C54"
                  testid="stat-queued" />
        <StatTile icon={Trophy} label="Quiz completions" value={funnel.quiz_completions || 0}
                  sub={`${Math.max(0, (funnel.subscribers_active || 0) - (funnel.quiz_completions || 0))} via PDF only`}
                  testid="stat-quiz" />
        <StatTile icon={TrendingUp} label="Active subscribers" value={funnel.subscribers_active || 0}
                  sub={`${(funnel.subscribers_total || 0) - (funnel.subscribers_active || 0)} unsubscribed`}
                  testid="stat-subscribers" />
      </div>

      {/* ----- BY EMAIL KIND ----- */}
      <Card className="border-border">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading text-base font-bold">By email in the sequence</h3>
            </div>
            <div className="text-[11px] text-foreground/55 font-mono-accent uppercase tracking-wider">
              Status breakdown
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {KIND_ORDER.map((kind) => {
              const k = stats.by_kind?.[kind] || {};
              const total = k.total || 0;
              return (
                <div key={kind} className="rounded-xl border border-border p-4" data-testid={`drip-kind-${kind}`}>
                  <div className="font-mono-accent uppercase tracking-[0.18em] text-[10px] text-[#004C54] font-bold">
                    {KIND_LABEL[kind]}
                  </div>
                  <div className="font-heading text-2xl font-bold mt-1">{total}</div>
                  <div className="text-[11px] text-foreground/60 mt-0.5">total scheduled</div>
                  <div className="mt-3 space-y-1.5">
                    {STATUS_ORDER.map((s) => (k[s] || 0) > 0 ? (
                      <div key={s} className="flex items-center justify-between text-xs">
                        <StatusPill status={s} />
                        <span className="tabular-nums text-foreground/70">{k[s] || 0}</span>
                      </div>
                    ) : null)}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ----- TIMELINE ----- */}
      <Card className="border-border">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-[#004C54]" />
            <h3 className="font-heading text-base font-bold">Sends — last 14 days</h3>
          </div>
          <Timeline days={stats.timeline_14d || []} />
        </CardContent>
      </Card>

      {/* ----- BY ARCHETYPE + SPORT ----- */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading text-base font-bold">Quiz takers — by archetype</h3>
            </div>
            {Object.keys(stats.by_archetype || {}).length === 0 ? (
              <div className="text-sm text-foreground/55" data-testid="archetype-empty">
                No quiz completions yet.
              </div>
            ) : (
              <div className="space-y-3">
                {archetypeEntries.map(([k, c]) => (
                  <Bar key={k} label={ARCHETYPE_LABEL[k] || k} count={c} max={archetypeMax}
                       testid={`archetype-bar-${k}`} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading text-base font-bold">Quiz takers — by sport</h3>
            </div>
            {Object.keys(stats.by_sport || {}).length === 0 ? (
              <div className="text-sm text-foreground/55" data-testid="sport-empty">
                No quiz completions yet.
              </div>
            ) : (
              <div className="space-y-3">
                {sportEntries.map(([k, c]) => (
                  <Bar key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}
                       count={c} max={sportMax} testid={`sport-bar-${k}`} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ----- RECENT DRIP ROWS ----- */}
      <Card className="border-border">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading text-base font-bold">Recent drip rows</h3>
            </div>
            <span className="text-[11px] text-foreground/55 font-mono-accent uppercase tracking-wider">
              Newest first
            </span>
          </div>
          {!recent?.length ? (
            <div className="text-sm text-foreground/55 py-6 text-center" data-testid="recent-empty">
              No drip rows yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drips-recent-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-foreground/55 border-b border-border">
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Kind</th>
                    <th className="py-2 pr-3">Archetype</th>
                    <th className="py-2 pr-3">Sport</th>
                    <th className="py-2 pr-3">Scheduled</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} className="border-b border-border/40 hover:bg-foreground/[0.02]" data-testid={`drip-row-${r.id}`}>
                      <td className="py-2 pr-3 font-mono-accent text-xs truncate max-w-[200px]" title={r.email}>{r.email}</td>
                      <td className="py-2 pr-3 text-xs">{KIND_LABEL[r.kind] || r.kind}</td>
                      <td className="py-2 pr-3 text-xs">{r.archetype ? (ARCHETYPE_LABEL[r.archetype] || r.archetype) : '—'}</td>
                      <td className="py-2 pr-3 text-xs">{r.sport || '—'}</td>
                      <td className="py-2 pr-3 text-xs text-foreground/60 tabular-nums whitespace-nowrap">
                        {r.scheduled_for ? new Date(r.scheduled_for).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                        }) : '—'}
                      </td>
                      <td className="py-2 pr-3"><StatusPill status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
